import { useState, useRef } from 'react';
import styles from './AdvancedWeightSettings.module.css';
import tuningIcon from '../img/tuning-4-svgrepo-com.svg';

/** Freie Gewichts-Map — bewusst weiter als AdvancedWeights, da MultiView eigene Keys hat. */
type WeightsMap = Record<string, number>;

// Standardwerte für die absoluten Weights (Standard-Solver)
const DEFAULT_WEIGHTS_STANDARD: WeightsMap = {
  weight_active_days: 5000,
  weight_unscheduled: 100000,
  weight_num_blocks: 100,
  weight_gap_time: 10,
  weight_total_span: 1,
  weight_preferred_times: 800,
  weight_balance: 0
};

// Standardwerte für MultiView (andere Werte)
const DEFAULT_WEIGHTS_MULTIVIEW: WeightsMap = {
  weight_active_days: 1000,
  weight_unscheduled: 100000,
  weight_num_blocks: 100,
  weight_gap_time: 10,
  weight_total_span: 1,
  weight_balance: 10000
};

// Beschreibungen für jeden Parameter
const DESCRIPTIONS: Record<string, string> = {
  weight_active_days: 'Steuert die Verteilung auf Wochentage. Positive Werte = weniger Tage, negative Werte = mehr Tage.',
  weight_unscheduled: 'Priorität: Maximierung der Anzahl eingeplanter Studierender. Höchste Priorität - sollte hoch bleiben.',
  weight_num_blocks: 'Priorität: Konsolidierung in weniger Blöcke. Höhere Werte = kompaktere Zeitblöcke.',
  weight_gap_time: 'Priorität: Minimierung von Lücken zwischen erster und letzter Stunde eines Tages.',
  weight_total_span: 'Priorität: Minimierung der Gesamtspanne pro Tag. Niedrigste Priorität.',
  weight_preferred_times: 'Priorität: Bevorzugung von Lieblingszeiten der Studierenden. Höhere Werte = stärkere Berücksichtigung der Favoriten (nur Solo-Solver).',
  weight_balance: 'Priorität: Gleichmäßige Verteilung der Studierenden auf die Unterrichtstage. Höhere Werte = ausgeglichenere Tage.'
};

interface AdvancedWeightSettingsProps {
  weights?: WeightsMap | null;
  onWeightsChange: (weights: WeightsMap) => void;
  isMultiView?: boolean;
  allowBlindAssignment?: boolean;
  onAllowBlindAssignmentChange: (value: boolean) => void;
  teacherTimeExtension?: number;
  onTeacherTimeExtensionChange: (value: number) => void;
}

function AdvancedWeightSettings({
  weights,
  onWeightsChange,
  isMultiView = false,
  allowBlindAssignment,
  onAllowBlindAssignmentChange,
  teacherTimeExtension,
  onTeacherTimeExtensionChange
}: AdvancedWeightSettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Wähle die richtigen Standardwerte basierend auf isMultiView
  const DEFAULT_WEIGHTS = isMultiView ? DEFAULT_WEIGHTS_MULTIVIEW : DEFAULT_WEIGHTS_STANDARD;

  // Initialisiere weights mit Standardwerten (direkte absolute Werte)
  const currentWeights = weights || DEFAULT_WEIGHTS;

  // Keys that support negative values (bidirectional slider)
  const BIDIRECTIONAL_KEYS = new Set(['weight_active_days']);
  const MIN_BIDIRECTIONAL = -50000;

  const handleValueChange = (key: string, value: string | number) => {
    // Validiere und begrenze den Wert
    let numValue = parseFloat(String(value));
    if (isNaN(numValue)) numValue = 0;
    const minVal = BIDIRECTIONAL_KEYS.has(key) ? MIN_BIDIRECTIONAL : 0;
    numValue = Math.max(minVal, Math.min(100000, numValue));

    const newWeights = {
      ...currentWeights,
      [key]: numValue
    };
    onWeightsChange(newWeights);
  };

  const handleSliderChange = (key: string, value: string) => {
    handleValueChange(key, value);
  };

  const handleInputChange = (key: string, value: string) => {
    handleValueChange(key, value);
  };

  const resetAllToDefaults = () => {
    const resetWeights: WeightsMap = {};
    weightKeys.forEach(key => {
      resetWeights[key] = DEFAULT_WEIGHTS[key] || 0;
    });
    onWeightsChange(resetWeights);
  };

  const weightKeys = isMultiView
    ? ['weight_active_days', 'weight_unscheduled', 'weight_num_blocks', 'weight_gap_time', 'weight_total_span', 'weight_balance']
    : ['weight_active_days', 'weight_unscheduled', 'weight_num_blocks', 'weight_gap_time', 'weight_total_span', 'weight_preferred_times', 'weight_balance'];

  const getLabel = (key: string): string => {
    const labels: Record<string, string> = {
      weight_active_days: 'Unterrichtstage',
      weight_unscheduled: 'Nicht eingeplante Studierende',
      weight_num_blocks: 'Anzahl Blöcke',
      weight_gap_time: 'Lückenzeit',
      weight_total_span: 'Gesamtspanne / Tag',
      weight_preferred_times: 'Lieblingszeiten',
      weight_balance: 'Ausgewogene Verteilung'
    };
    return labels[key] || key;
  };

  const getExtremes = (key: string): { left: string; right: string } | null => {
    const extremes: Record<string, { left: string; right: string }> = {
      weight_active_days:      { left: 'mehr Tage',        right: 'weniger Tage' },
      weight_unscheduled:      { left: 'weniger einplanen', right: 'alle einplanen' },
      weight_num_blocks:       { left: 'mehr Blöcke',       right: 'weniger Blöcke' },
      weight_gap_time:         { left: 'Lücken egal',       right: 'Lücken vermeiden' },
      weight_total_span:       { left: 'Spanne egal',       right: 'Spanne minimieren' },
      weight_preferred_times:  { left: 'ignorieren',        right: 'stark beachten' },
      weight_balance:          { left: 'ungleich',          right: 'gleichmäßig' },
    };
    return extremes[key] || null;
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={`${styles.header} ${isExpanded ? styles.expanded : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
        aria-label="Erweiterte Einstellungen"
        title="Erweiterte Einstellungen"
      >
        <img src={tuningIcon} alt="" aria-hidden="true" className={styles.settingsIcon} />
      </button>
      
      {isExpanded && (
        <div className={styles.content} ref={popupRef}>
          {/* General Settings */}
          <div className={styles.generalSettings}>
            <div className={styles.settingRow}>
              <label htmlFor="allowBlindAssignment" className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  id="allowBlindAssignment"
                  checked={allowBlindAssignment}
                  onChange={(e) => onAllowBlindAssignmentChange(e.target.checked)}
                  className={styles.checkbox}
                />
                <span>Ohne Zeiten blind einsetzen</span>
              </label>
              <span className={styles.infoIcon}>
                ℹ️
                <span className={styles.tooltip}>
                  Wenn aktiviert, werden Studierende ohne eingetragene Zeiten so behandelt, als hätten sie immer dann Zeit, wenn du Zeit hast — sie bekommen also trotzdem einen Termin.<br/><br/>
                  Ob jemand registriert ist, spielt dabei keine Rolle: Entscheidend ist allein, dass keine Zeiten hinterlegt sind.
                </span>
              </span>
            </div>

            <div className={styles.settingRow}>
              <label htmlFor="teacherTimeExtension" className={styles.selectLabel}>
                Zeitverlängerung Lehrer
              </label>
              <span className={styles.infoIcon}>
                ℹ️
                <span className={styles.tooltip}>
                  Verlängert die Verfügbarkeitszeiten des Lehrers automatisch um die gewählte Zeit vor und nach den angegebenen Zeiten.<br/><br/>
                  Dies schafft mehr Spielraum für die automatische Verteilung.<br/><br/>
                  Beispiel: Bei 30 Min. wird 9:00-12:00 zu 8:30-12:30
                </span>
              </span>
              <select
                id="teacherTimeExtension"
                value={teacherTimeExtension}
                onChange={(e) => onTeacherTimeExtensionChange(Number(e.target.value))}
                className={styles.select}
              >
                <option value={0}>Keine Verlängerung</option>
                <option value={15}>± 15 Minuten</option>
                <option value={30}>± 30 Minuten</option>
                <option value={45}>± 45 Minuten</option>
                <option value={60}>± 60 Minuten</option>
              </select>
            </div>
          </div>

          <div className={styles.divider}></div>

          {/* Weight Settings */}
          {weightKeys.map((key) => {
            const currentValue = currentWeights[key] || 0;
            const defaultValue = DEFAULT_WEIGHTS[key] || 0;
            const extremes = getExtremes(key);
            
            return (
              <div key={key} className={styles.weightRow}>
                <div className={styles.weightHeader}>
                  <label className={styles.label} htmlFor={key}>
                    {getLabel(key)}
                  </label>
                  <span className={styles.infoIcon}>
                    ℹ️
                    <span className={styles.tooltip}>
                      {DESCRIPTIONS[key]}
                    </span>
                  </span>
                </div>
                
                <div className={styles.controlsRow}>
                  <div className={styles.sliderWrapper}>
                    <input
                      type="range"
                      id={`${key}-slider`}
                      min={BIDIRECTIONAL_KEYS.has(key) ? MIN_BIDIRECTIONAL : 0}
                      max="100000"
                      step="100"
                      value={currentValue}
                      onChange={(e) => handleSliderChange(key, e.target.value)}
                      className={styles.slider}
                    />
                    {extremes && (
                      <div className={styles.sliderExtremes}>
                        <span>{extremes.left}</span>
                        <span>{extremes.right}</span>
                      </div>
                    )}
                  </div>

                  <div className={styles.inputGroup}>
                    <input
                      type="number"
                      id={`${key}-input`}
                      min={BIDIRECTIONAL_KEYS.has(key) ? MIN_BIDIRECTIONAL : 0}
                      max="100000"
                      step="1"
                      value={Math.round(currentValue)}
                      onChange={(e) => handleInputChange(key, e.target.value)}
                      className={styles.numberInput}
                    />
                    <span className={styles.defaultInfo}>
                      Standard: {defaultValue}
                    </span>
                  </div>
                  
                </div>
              </div>
            );
          })}
          
          <button
            className={styles.resetAllButton}
            onClick={resetAllToDefaults}
            type="button"
          >
            Alle auf Standardwerte zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
}

export default AdvancedWeightSettings;
